import { Spinner } from '@fluentui/react-components';
import cn from 'classnames';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import './Loader.css';

/** Loader size options */
export type LoaderSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** Label position relative to spinner */
export type LoaderLabelPosition = 'above' | 'below' | 'before' | 'after';

// Fluent UI internal type (not exported)
type FluentSpinnerSize =
  | 'extra-tiny'
  | 'tiny'
  | 'extra-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'extra-large'
  | 'huge';

// Internal mapping: our sizes to Fluent sizes
const sizeToFluentSize: Record<LoaderSize, FluentSpinnerSize> = {
  xs: 'extra-small',
  sm: 'small',
  md: 'medium',
  lg: 'large',
  xl: 'extra-large',
};

export interface LoaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Loader size */
  size?: LoaderSize;
  /** Visible label text displayed alongside the spinner */
  label?: string;
  /** Position of the label relative to the spinner */
  labelPosition?: LoaderLabelPosition;
  /** Center the loader (full width) */
  centered?: boolean;
  /** Inverted colors for dark backgrounds */
  inverted?: boolean;
}

/**
 * Loader component with semantic API.
 * Implementation uses Fluent UI internally but exposes a library-agnostic interface.
 *
 * Includes a default aria-label for accessibility. Can be overridden via aria-label prop.
 *
 * @example
 * // Basic usage (uses default aria-label)
 * <Loader />
 *
 * @example
 * // Small size for inline/button use
 * <Loader size="sm" />
 *
 * @example
 * // With visible label
 * <Loader label="Loading..." />
 *
 * @example
 * // Centered full-width loader
 * <Loader centered />
 */
export const Loader = ({
  size = 'md',
  label,
  labelPosition = 'after',
  centered = false,
  inverted = false,
  className,
  'aria-label': ariaLabel,
  ...rest
}: LoaderProps) => {
  const { t } = useTranslation();

  // Map to Fluent UI internals (implementation detail)
  const fluentSize = sizeToFluentSize[size];

  // Default aria-label for accessibility
  const defaultAriaLabel = t('loader_aria_label', {
    defaultValue: 'Fortschrittsanzeige/Ladebildschirm',
  });

  return (
    <Spinner
      size={fluentSize}
      label={label}
      labelPosition={labelPosition}
      appearance={inverted ? 'inverted' : 'primary'}
      className={cn('aix-loader', centered && 'aix-loader--centered', className)}
      aria-label={ariaLabel ?? defaultAriaLabel}
      {...rest}
    />
  );
};
